// M29 — Forgot password page. Renders an email-input form that calls
// `resetPasswordForEmail`, which has Supabase send a recovery email
// with a link back to /reset-password. On success the page collapses
// into a "Check your email" confirmation so the user knows what to do
// next without us having to navigate them anywhere.
//
// We always show success even if the email was invalid — leaking which
// emails are registered would be an account-enumeration vector. Supabase
// silently no-ops for unknown emails, so the UI just mirrors that.

import { useState, type FormEvent } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import CircularProgress from '@mui/material/CircularProgress';
import QSLogo from '../../components/QSLogo';
import AuthPageFooter from '../../components/AuthPageFooter';
import { useAuth } from '../../contexts/useAuth';
import { friendlyAuthError } from '../../lib/authErrors';

export default function ForgotPasswordPage() {
  const { resetPasswordForEmail } = useAuth();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    const { error: err } = await resetPasswordForEmail(email.trim());
    setLoading(false);
    if (err) {
      setError(friendlyAuthError(err.message));
      return;
    }
    setSubmitted(true);
  }

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      bgcolor: 'background.default',
      px: 2,
      py: 4,
      gap: 3,
    }}>
      <Card sx={{ maxWidth: 420, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <QSLogo variant="icon" height={52} />
            <Box sx={{ mt: 1.5, mb: 0.5 }}>
              <QSLogo variant="wordmark" />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {submitted ? 'Check your email' : 'Reset your password'}
            </Typography>
          </Box>

          {submitted ? (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                If an account exists for <strong>{email}</strong>, we just sent a link
                to reset your password. The link is valid for a short time.
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Don't see it within a minute or two? Check your spam folder, or try
                requesting the link again.
              </Typography>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => { setSubmitted(false); setEmail(''); }}
              >
                Send another link
              </Button>
            </>
          ) : (
            <>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter the email you signed up with and we'll send you a reset link.
              </Typography>
              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                  sx={{ mb: 3 }}
                />
                <Button type="submit" variant="contained" fullWidth size="large" disabled={loading}>
                  {loading ? <CircularProgress size={24} /> : 'Send reset link'}
                </Button>
              </Box>
            </>
          )}

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Remembered it?{' '}
              <Link component={RouterLink} to="/login">Back to sign in</Link>
            </Typography>
          </Box>
        </CardContent>
      </Card>
      <AuthPageFooter />
    </Box>
  );
}
